import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchEmployees } from "../../redux/employer-employeeSlice/EmployerEmployeeSlice";
import { toast } from "sonner";
import axios from "axios";
import PaystackPop from "@paystack/inline-js";

const JobSeekerBenefits = () => {
  const dispatch = useDispatch();
  const { employees, status, error } = useSelector((state) => state.employerEmployee || {});
  const { currentUser } = useSelector((state) => state.user || {});
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(false);

  // Fetch employee data and benefits
  useEffect(() => {
    if (!currentUser?._id) {
      toast.error("User not authenticated");
      return;
    }

    // Fetch employee data
    dispatch(fetchEmployees({ id: currentUser._id, userType: "jobSeeker" }))
      .unwrap()
      .then((data) => {
        const employeeData = Array.isArray(data) ? data.find((emp) => emp._id === currentUser._id) : data;
        if (employeeData && employeeData._id) {
          setEmployee(employeeData);
        } else {
          toast.error("No employee data found");
        }
      })
      .catch((err) => {
        toast.error("Failed to load employee data");
      });

    // Fetch benefits
    const fetchBenefits = async () => {
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}benefit/getUserBenefit`,
          { applicantId: currentUser._id },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        setEmployee((prev) => ({ ...prev, benefits: response.data.data }));
      } catch (err) {
        toast.error("Failed to load benefits");
      }
    };
    fetchBenefits();
  }, [currentUser?._id, dispatch]);

  const handlePayBenefit = async (amount, type, period) => {
    if (!employee) {
      toast.error("No employee data available");
      return;
    }

    setLoading(true);
    const paystack = new PaystackPop();
    paystack.newTransaction({
      key: process.env.REACT_APP_PAYSTACK_PUBLIC_KEY,
      email: currentUser.email,
      amount: Math.round(parseFloat(amount) * 100), 
      metadata: {
        applicantId: currentUser._id,
        benefitType: type,
        period,
      },
      onSuccess: async (transaction) => {
        try {
          // Verify payment with Paystack
          const paystackResponse = await axios.get(
            `https://api.paystack.co/transaction/verify/${transaction.reference}`,
            {
              headers: {
                Authorization: `Bearer ${process.env.REACT_APP_PAYSTACK_SECRET_KEY}`,
              },
            }
          );

          if (paystackResponse.data.data.status !== "success") {
            throw new Error("Payment verification failed");
          }

          // Create benefit record
          const response = await axios.post(
            `${process.env.REACT_APP_API_URL}benefit/create`,
            {
              applicantId: currentUser._id,
              benefitType: type,
              benefitDescription: `${type} benefit for ${period}`,
              amountRequested: amount.toString(),
              benefitBalance: "0",
              benefitStatus: "Pending",
            },
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            }
          );

          // Update job seeker's benefits array
          await axios.patch(
            `${process.env.REACT_APP_API_URL}jobseeker/updateJobSeeker/${currentUser._id}`,
            { benefits: [...(employee.benefits || []), response.data.data._id] },
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            }
          );

          setEmployee((prev) => ({
            ...prev,
            benefits: [...(prev.benefits || []), response.data.data],
          }));
          toast.success(`Payment for ${type} benefit submitted for approval`);
        } catch (err) {
          toast.error("Payment verification or benefit creation failed");
        } finally {
          setLoading(false);
        }
      },
      onCancel: () => {
        toast.info("Payment cancelled");
        setLoading(false);
      },
    });
  };

  const calculateBenefits = (grossSalary) => ({
    essential: parseFloat(grossSalary || 0) * 0.1,
    health: parseFloat(grossSalary || 0) * 0.05,
  });

  const getTotalBenefits = () => {
    const totals = { essential: 0, health: 0, all: 0 };
    employee?.benefits?.forEach((benefit) => {
      if (benefit.benefitStatus === "Approved") {
        const amount = parseFloat(benefit.amountRequested);
        totals[benefit.benefitType] += amount;
        totals.all += amount;
      }
    });
    return totals;
  };

  if (status === "loading" && !employee) {
    return <div className="flex items-center justify-center h-screen bg-gray-100">Loading...</div>;
  }

  if (error && !employee) {
    return (
      <div className="flex items-center justify-center h-screen text-red-500 bg-gray-100">
        {typeof error === "string" ? error : "An error occurred while loading data"}
      </div>
    );
  }

  // return (
  //   <div className="min-h-screen bg-gray-100 p-6">
  //     <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">Benefits Management</h2>
  //     {employee ? (
  //       <div className="bg-white rounded-lg shadow-lg p-6">
  //         <h3 className="text-2xl font-semibold text-gray-700 mb-4">Salary and Benefits</h3>
  //         <div className="overflow-x-auto">
  //           <table className="w-full bg-gray-50 border border-gray-200">
  //             <thead className="bg-orange-400 text-white">
  //               <tr>
  //                 <th className="py-3 px-4 text-left">Period</th>
  //                 <th className="py-3 px-4 text-left">Gross Salary (₦)</th>
  //                 <th className="py-3 px-4 text-left">Essential Benefit (10%)</th>
  //                 <th className="py-3 px-4 text-left">Health Benefit (5%)</th>
  //                 <th className="py-3 px-4 text-left">Total</th>
  //                 <th className="py-3 px-4 text-left">Actions</th>
  //               </tr>
  //             </thead>
  //             <tbody>
  //               {employee.salary?.history?.length > 0 ? (
  //                 employee.salary.history.map((hist, i) => {
  //                   const benefits = calculateBenefits(hist.grossSalary);
  //                   return (
  //                     <tr key={i} className="border-t hover:bg-gray-100">
  //                       <td className="py-3 px-4">{`${hist.month} ${hist.year}`}</td>
  //                       <td className="py-3 px-4">{(hist.grossSalary || 0).toLocaleString()}</td>
  //                       <td className="py-3 px-4">{benefits.essential.toLocaleString()}</td>
  //                       <td className="py-3 px-4">{benefits.health.toLocaleString()}</td>
  //                    <td className="py-3 px-4">{(benefits.health + benefits.essential).toLocaleString()}</td>
  //                       <td className="py-3 px-4 flex gap-2">
  //                         <button
  //                           onClick={() => handlePayBenefit(benefits.essential, "essential", `${hist.month} ${hist.year}`)}
  //                           disabled={loading}
  //                           className={`px-4 py-2 rounded-lg text-white ${
  //                             loading ? "bg-gray-400 cursor-not-allowed" : "bg-green-500 hover:bg-green-600"
  //                           }`}
  //                         >
  //                           Pay Essential
  //                         </button>
  //                         <button
  //                           onClick={() => handlePayBenefit(benefits.health, "health", `${hist.month} ${hist.year}`)}
  //                           disabled={loading}
  //                           className={`px-4 py-2 rounded-lg text-white ${
  //                             loading ? "bg-gray-400 cursor-not-allowed" : "bg-blue-500 hover:bg-blue-600"
  //                           }`}
  //                         >
  //                           Pay Health
  //                         </button>
  //                       </td>
  //                     </tr>
  //                   );
  //                 })
  //               ) : (
  //                 <tr>
  //                   <td colSpan="5" className="py-3 px-4 text-center text-gray-500">
  //                     No salary history available
  //                   </td>
  //                 </tr>
  //               )}
  //             </tbody>
  //           </table>
  //         </div>

  //         <h3 className="text-2xl font-semibold text-gray-700 mt-6 mb-4">Benefit Payment History</h3>
  //         <div className="overflow-x-auto">
  //           <table className="w-full bg-gray-50 border border-gray-200">
  //             <thead className="bg-orange-400 text-white">
  //               <tr>
  //                 <th className="py-3 px-4 text-left">Type</th>
  //                 <th className="py-3 px-4 text-left">Amount (₦)</th>
  //                 <th className="py-3 px-4 text-left">Period</th>
  //                 <th className="py-3 px-4 text-left">Status</th>
  //                 <th className="py-3 px-4 text-left">Date Requested</th>
  //               </tr>
  //             </thead>
  //             <tbody>
  //               {employee.benefits?.length > 0 ? (
  //                 employee.benefits.map((benefit, i) => (
  //                   <tr key={i} className="border-t hover:bg-gray-100">
  //                     <td className="py-3 px-4 capitalize">{benefit.benefitType}</td>
  //                     <td className="py-3 px-4">{parseFloat(benefit.amountRequested).toLocaleString()}</td>
  //                     <td className="py-3 px-4">{benefit.benefitDescription.split(" for ")[1] || "N/A"}</td>
  //                     <td className="py-3 px-4">
  //                       <span
  //                         className={
  //                           benefit.benefitStatus === "Approved"
  //                             ? "text-green-600"
  //                             : benefit.benefitStatus === "Rejected"
  //                             ? "text-red-600"
  //                             : "text-yellow-600"
  //                         }
  //                       >
  //                         {benefit.benefitStatus}
  //                       </span>
  //                     </td>
  //                     <td className="py-3 px-4">{new Date(benefit.createdAt).toLocaleString()}</td>
  //                   </tr>
  //                 ))
  //               ) : (
  //                 <tr>
  //                   <td colSpan="5" className="py-3 px-4 text-center text-gray-500">
  //                     No benefit payments made
  //                   </td>
  //                 </tr>              )}
  //             </tbody>
  //           </table>
  //         </div>

  //         <div className="mt-4 text-lg font-semibold">
  //           <p>Total Essential Benefit Paid: ₦{getTotalBenefits().essential.toLocaleString()}</p>
  //           <p>Total Health Benefit Paid: ₦{getTotalBenefits().health.toLocaleString()}</p>
  //           <p>Total All Benefits Paid: ₦{getTotalBenefits().all.toLocaleString()}</p>
  //         </div>
  //       </div>
  //     ) : (
  //       <p className="text-gray-500 text-center">No employee data available. Please contact your employer.</p>
  //     )}
  //   </div>
  // );

  return (
  <div className="min-h-screen bg-gray-50 px-4 py-6 md:p-8">
    <div className="max-w-6xl mx-auto">
      <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-8 text-center">
        Benefits Management
      </h2>

      {employee ? (
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {/* Salary & Benefits Table */}
          <div className="p-6 border-b">
            <h3 className="text-xl md:text-2xl font-semibold text-gray-800 mb-5">
              Salary & Calculated Benefits
            </h3>

            <div className="overflow-x-auto -mx-6 md:mx-0">
              <div className="inline-block min-w-full align-middle">
                <table className="min-w-[850px] md:min-w-full divide-y divide-gray-200">
                  <thead className="bg-gradient-to-r from-orange-500 to-orange-600 text-white">
                    <tr>
                      <th className="px-4 py-4 text-left text-sm font-semibold uppercase tracking-wider whitespace-nowrap">
                        Period
                      </th>
                      <th className="px-4 py-4 text-left text-sm font-semibold uppercase tracking-wider whitespace-nowrap">
                        Gross Salary (₦)
                      </th>
                      <th className="px-4 py-4 text-left text-sm font-semibold uppercase tracking-wider whitespace-nowrap">
                        Essential (10%)
                      </th>
                      <th className="px-4 py-4 text-left text-sm font-semibold uppercase tracking-wider whitespace-nowrap">
                        Health (5%)
                      </th>
                      <th className="px-4 py-4 text-left text-sm font-semibold uppercase tracking-wider whitespace-nowrap">
                        Total Benefit
                      </th>
                      <th className="px-4 py-4 text-left text-sm font-semibold uppercase tracking-wider whitespace-nowrap">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {employee.salary?.history?.length > 0 ? (
                      employee.salary.history.map((hist, i) => {
                        const benefits = calculateBenefits(hist.grossSalary);
                        return (
                          <tr key={i} className="hover:bg-orange-50/30 transition-colors">
                            <td className="px-4 py-4 text-sm text-gray-700 whitespace-nowrap">
                              {hist.month} {hist.year}
                            </td>
                            <td className="px-4 py-4 text-sm font-medium text-gray-900 whitespace-nowrap">
                              ₦{(hist.grossSalary || 0).toLocaleString()}
                            </td>
                            <td className="px-4 py-4 text-sm text-gray-900 whitespace-nowrap">
                              ₦{benefits.essential.toLocaleString()}
                            </td>
                            <td className="px-4 py-4 text-sm text-gray-900 whitespace-nowrap">
                              ₦{benefits.health.toLocaleString()}
                            </td>
                            <td className="px-4 py-4 text-sm font-medium text-gray-900 whitespace-nowrap">
                              ₦{(benefits.essential + benefits.health).toLocaleString()}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap">
                              <div className="flex flex-col sm:flex-row gap-2">
                                <button
                                  onClick={() =>
                                    handlePayBenefit(
                                      benefits.essential,
                                      "essential",
                                      `${hist.month} ${hist.year}`
                                    )
                                  }
                                  disabled={loading}
                                  className={`px-4 py-2 rounded-lg text-white text-sm font-medium transition-colors ${
                                    loading
                                      ? "bg-gray-400 cursor-not-allowed"
                                      : "bg-green-600 hover:bg-green-700"
                                  }`}
                                >
                                  Pay Essential
                                </button>
                                <button
                                  onClick={() =>
                                    handlePayBenefit(
                                      benefits.health,
                                      "health",
                                      `${hist.month} ${hist.year}`
                                    )
                                  }
                                  disabled={loading}
                                  className={`px-4 py-2 rounded-lg text-white text-sm font-medium transition-colors ${
                                    loading
                                      ? "bg-gray-400 cursor-not-allowed"
                                      : "bg-blue-600 hover:bg-blue-700"
                                  }`}
                                >
                                  Pay Health
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={6} className="py-12 text-center text-gray-500">
                          No salary history available yet
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Benefit Payment History */}
          <div className="p-6">
            <h3 className="text-xl md:text-2xl font-semibold text-gray-800 mb-5">
              Benefit Payment History
            </h3>

            <div className="overflow-x-auto -mx-6 md:mx-0">
              <div className="inline-block min-w-full align-middle">
                <table className="min-w-[750px] md:min-w-full divide-y divide-gray-200">
                  <thead className="bg-gradient-to-r from-orange-500 to-orange-600 text-white">
                    <tr>
                      <th className="px-4 py-4 text-left text-sm font-semibold uppercase tracking-wider whitespace-nowrap">
                        Type
                      </th>
                      <th className="px-4 py-4 text-left text-sm font-semibold uppercase tracking-wider whitespace-nowrap">
                        Amount (₦)
                      </th>
                      <th className="px-4 py-4 text-left text-sm font-semibold uppercase tracking-wider whitespace-nowrap">
                        Period
                      </th>
                      <th className="px-4 py-4 text-left text-sm font-semibold uppercase tracking-wider whitespace-nowrap">
                        Status
                      </th>
                      <th className="px-4 py-4 text-left text-sm font-semibold uppercase tracking-wider whitespace-nowrap">
                        Date
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {employee.benefits?.length > 0 ? (
                      employee.benefits.map((benefit, i) => (
                        <tr key={i} className="hover:bg-gray-50 transition-colors">
                          <td className="px-4 py-4 text-sm font-medium capitalize text-gray-900">
                            {benefit.benefitType}
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-900 whitespace-nowrap">
                            ₦{parseFloat(benefit.amountRequested).toLocaleString()}
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-700 whitespace-nowrap">
                            {benefit.benefitDescription.split(" for ")[1] || "N/A"}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <span
                              className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${
                                benefit.benefitStatus === "Approved"
                                  ? "bg-green-100 text-green-800"
                                  : benefit.benefitStatus === "Rejected"
                                  ? "bg-red-100 text-red-800"
                                  : "bg-yellow-100 text-yellow-800"
                              }`}
                            >
                              {benefit.benefitStatus}
                            </span>
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-600 whitespace-nowrap">
                            {new Date(benefit.createdAt).toLocaleDateString("en-GB", {
                              day: "numeric",
                              month: "short",
                              year: "numeric",
                            })}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={5} className="py-12 text-center text-gray-500">
                          No benefit payments made yet
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Summary Totals */}
            <div className="mt-8 p-6 bg-gray-50 rounded-lg border border-gray-200">
              <h4 className="text-lg font-semibold text-gray-800 mb-4">Payment Summary</h4>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 text-center">
                <div>
                  <p className="text-sm text-gray-600">Total Essential</p>
                  <p className="text-2xl font-bold text-green-600">
                    ₦{getTotalBenefits().essential.toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Total Health</p>
                  <p className="text-2xl font-bold text-blue-600">
                    ₦{getTotalBenefits().health.toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Grand Total</p>
                  <p className="text-2xl font-bold text-gray-800">
                    ₦{getTotalBenefits().all.toLocaleString()}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow p-8 text-center">
          <p className="text-lg text-gray-600">
            No employee data available yet.<br />
            Please contact your employer to get set up.
          </p>
        </div>
      )}
    </div>
  </div>
);
};

export default JobSeekerBenefits;